/** Share is a bounded snapshot consumer, never a cart mutation or cart lock. */
(function () {
  if (window.__drhCartShareListenerBound) return;
  window.__drhCartShareListenerBound = true;
  let pending = false;
  let generation = 0;
  let prepared = null;
  const english = document.documentElement.lang?.toLowerCase().startsWith('en');
  const messages = english ? {
    error: 'The cart could not be shared. Please try again.',
    unsupported: 'This cart contains a subscription or item options that a share link cannot preserve.',
    ready: 'Link ready. It contains items and quantities, without a shipping or discount promise.',
    action: 'Share or copy link', copied: 'Link copied!',
  } : {
    error: 'Der Warenkorb konnte nicht geteilt werden. Bitte versuchen Sie es erneut.',
    unsupported: 'Dieser Warenkorb enthält ein Abo oder Artikeloptionen, die ein Teilen-Link nicht erhalten kann.',
    ready: 'Link bereit. Er enthält Artikel und Mengen, keine Versand- oder Rabattzusage.',
    action: 'Link teilen oder kopieren', copied: 'Link kopiert!',
  };

  function feedback(message, action) {
    const root = document.querySelector('[data-cart-drawer][aria-hidden="false"]')
      || document.querySelector('[data-cart-page]') || document.body;
    const surface = root.querySelector('[data-cart-footer], [data-cart-summary]') || root;
    let status = surface.querySelector('[data-cart-share-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.cartShareStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      surface.append(status);
    }
    status.textContent = message;
    if (action) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = messages.action;
      button.addEventListener('click', action);
      status.append(' ', button);
    }
  }

  function unsupported(cart) {
    return cart.items.some((item) => item.selling_plan_allocation
      || Object.keys(item.properties || {}).length > 0);
  }

  function syncShareState(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const blocked = unsupported(cart);
    document.querySelectorAll('[data-cart-share]').forEach((button) => {
      button.disabled = blocked;
      button.hidden = blocked;
      button.removeAttribute('aria-describedby');
      if (blocked) button.title = messages.unsupported;
      else button.removeAttribute('title');
    });
    document.querySelectorAll('[data-cart-share-subscription-hint]').forEach((hint) => {
      hint.hidden = true;
    });
    return blocked;
  }

  async function readCart(url) {
    const controller = new AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
          if (!response.ok) throw new Error('Cart read failed');
          const cart = await response.json();
          if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart');
          return cart;
        })(),
        new Promise((_, reject) => {
          timer = setTimeout(() => { controller.abort(); reject(new Error('Cart read timed out')); }, 10000);
        }),
      ]);
    } finally { clearTimeout(timer); }
  }

  function current(snapshot) {
    return snapshot && snapshot.generation === generation
      && !window.CartOperations?.hasPending() && !window.CartOperations?.isBlocked();
  }

  async function publish() {
    const snapshot = prepared;
    if (!current(snapshot)) { prepared = null; feedback(messages.error); return; }
    // No cart lock while a person chooses/cancels a native share target.
    prepared = null;
    try {
      if (navigator.share) await navigator.share({ title: snapshot.title, url: snapshot.url });
      else { await navigator.clipboard.writeText(snapshot.url); feedback(messages.copied); }
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (error.name === 'NotAllowedError' && current(snapshot)) {
        prepared = snapshot;
        feedback(messages.ready, publish);
      } else feedback(messages.error);
    }
  }

  document.addEventListener('cart:changing', () => { generation += 1; prepared = null; });
  document.addEventListener('cart:updated', (event) => {
    generation += 1;
    prepared = null;
    syncShareState(event.detail);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target?.closest?.('[data-cart-share]');
    if (!button || !document.contains(button) || button.disabled || pending) return;
    event.preventDefault();
    if (current(prepared)) { void publish(); return; }
    pending = true;
    try {
      window.CartLineControls?.commitFocusedQuantity?.();
      const idle = window.CartOperations ? await window.CartOperations.waitForIdle()
        : window.CartLineControls?.hasPendingUpdates?.() ? await window.CartLineControls.waitForPendingUpdates() : true;
      if (!idle) throw new Error('Cart is not confirmed');
      const observed = generation;
      const root = String(window.Shopify?.routes?.root || '/').replace(/^\/+|\/+$/g, '');
      const localeRoot = root ? '/' + root + '/' : '/';
      const cart = await readCart(localeRoot + 'cart.js');
      if (observed !== generation || window.CartOperations?.hasPending()
        || window.CartOperations?.isBlocked()) throw new Error('Cart changed while preparing share');
      if (syncShareState(cart)) { feedback(messages.unsupported); return; }
      if (!cart.items.length) throw new Error('Empty cart');
      prepared = {
        generation, title: button.dataset.shareTitle || 'Warenkorb',
        url: window.location.origin + localeRoot + 'cart/'
          + cart.items.map((item) => item.variant_id + ':' + item.quantity).join(','),
      };
      // Async barriers/reads may outlive transient browser activation.
      if (navigator.userActivation && !navigator.userActivation.isActive) feedback(messages.ready, publish);
      else await publish();
    } catch { feedback(messages.error); }
    finally { pending = false; }
  });
})();
